import type * as C from "@zooi/contracts";
import { direction, type Metric } from "./metric-dashboard.ts";
import type { StatusKind } from "./ui/status-kinds.ts";

// Insights › Diagnosis (docs/design-system.md §0.4f, 2026-11-01): where the
// product stands at each stage of its growth, from the numbers its kind of
// product needs (the data setup's needs) and their latest complete range.
// It never names a cause or a "bottleneck" from numbers alone: it says what
// to connect first, then which number moved the wrong way, and only from
// complete ranges; a small sample gives a direction only.

/** The stages in the order a person reads a funnel. */
export const STAGES = ["reach", "acquisition", "activation", "retention", "revenue"] as const;
export type Stage = (typeof STAGES)[number];

/**
 * How one needed number reads now:
 * - `not_connected`: no metric answers it yet;
 * - `no_data`: a metric answers it, but there is no value in the range;
 * - `unknown`: a value, but no complete previous range to compare with;
 * - `better`, `worse`, `same`: against the previous range, in the metric's own direction.
 */
export type NeedState = "not_connected" | "no_data" | "unknown" | "better" | "worse" | "same";

export interface NeedRow {
  need: C.DataSetup["needs"][number]["need"];
  importance: "required" | "recommended";
  metric: Metric | null;
  state: NeedState;
  /** Fewer observations than the small-sample threshold: only the direction is read. */
  small: boolean;
}

export interface StageRow { stage: Stage; needs: NeedRow[]; status: StatusKind }

/** What to do first: connect a required number, fill one that has no data, or look at one that moved the wrong way. */
export type Attention =
  | { kind: "connect"; row: NeedRow; stage: Stage; missing: number }
  | { kind: "no_data"; row: NeedRow; stage: Stage }
  | { kind: "worse"; row: NeedRow; stage: Stage }
  | null;

export function needState(metric: Metric | null): NeedState {
  if (!metric) return "not_connected";
  if (metric.missing || metric.range.value === null) return "no_data";
  const moved = direction(metric.range);
  if (moved === null) return "unknown";
  if (moved === "same") return "same";
  return (moved === "higher") === (metric.definition.direction === "higher") ? "better" : "worse";
}

/** A stage's icon: the state that most needs the person, as the list's statuses (§0.3b). */
function stageStatus(rows: NeedRow[]): StatusKind {
  if (rows.some((r) => r.state === "not_connected" && r.importance === "required")) return "not_started";
  if (rows.some((r) => r.state === "no_data")) return "inconclusive";
  if (rows.some((r) => r.state === "worse" && !r.small)) return "negative";
  if (rows.some((r) => r.state === "better" && !r.small)) return "positive";
  return rows.every((r) => r.state === "not_connected") ? "not_started" : "none";
}

/** The icon of one number, by its state. */
export const NEED_STATUS: Record<NeedState, StatusKind> = {
  not_connected: "not_started", no_data: "inconclusive", unknown: "none", better: "positive", worse: "negative", same: "none",
};

/**
 * The product's stages, each with the numbers its kind of product needs, in
 * the data setup's order; stages it needs nothing for are left out. A metric
 * is matched by key among the dashboard's metrics; an archived metric does
 * not answer.
 */
export function diagnose(setup: Pick<C.DataSetup, "needs">, metrics: readonly Metric[]): { stages: StageRow[]; attention: Attention } {
  const byKey = new Map(metrics.filter((m) => !m.presentation?.archived_at).map((m) => [m.definition.key, m]));
  const stages = STAGES.map((stage) => {
    const needs = setup.needs.filter((n) => n.role === stage).map((n): NeedRow => {
      const metric = n.metric_key ? byKey.get(n.metric_key) ?? null : null;
      return { need: n.need, importance: n.importance, metric, state: needState(metric), small: Boolean(metric?.range.small_sample) };
    });
    return { stage, needs, status: stageStatus(needs) };
  }).filter((s) => s.needs.length > 0);
  const rows = stages.flatMap((s) => s.needs.map((row) => ({ row, stage: s.stage })));
  const missing = rows.filter(({ row }) => row.state === "not_connected" && row.importance === "required");
  const empty = rows.find(({ row }) => row.state === "no_data");
  const worse = rows.find(({ row }) => row.state === "worse" && !row.small);
  const attention: Attention = missing[0] ? { kind: "connect", ...missing[0], missing: missing.length }
    : empty ? { kind: "no_data", ...empty }
    : worse ? { kind: "worse", ...worse }
    : null;
  return { stages, attention };
}
